import { useEffect, useState } from 'react';
import type { OrderStatusId } from '@witmetrics/utils';
import { ORDER_STATUS_ID } from '@witmetrics/utils/constants';
import { useDispatch, useOrder } from '@witmetrics/data-store';
import { showSuccessToaster } from '@/utils/toasters';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { useDate } from '@/hooks/useDate';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import DateTimeInput from '@/components/inputs/DateTimeInput';
import StatusField from './StatusField';
import ExternalRefField from './ExternalRefField';
import { classes, updateOrder } from './utils';

const { PENDING } = ORDER_STATUS_ID;

export type EditLabOrderDialogProps = {
  orderId: number;
  onClose: () => void;
};

export default function EditLabOrderDialog({
  orderId,
  onClose,
}: EditLabOrderDialogProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const order = useOrder(orderId);
  const [orderStatusId, setOrderStatusId] = useState<OrderStatusId>(PENDING);
  const [externalRef, setExternalRef] = useState('');
  const [createdAt, setCreatedAt] = useDate(order?.createdAt);
  const [isSaving, toggleSaving] = useToggle(false);

  useEffect(() => {
    if (order) fillFields();
  }, [order]);

  const fillFields = () => {
    setOrderStatusId(order!.orderStatusId);
    setExternalRef(order!.externalRef || '');
    setCreatedAt(order!.createdAt);
  };

  const handleSave = async () => {
    try {
      toggleSaving(true);
      dispatch.addOrders([
        await updateOrder(orderId!, orderStatusId, externalRef, createdAt),
      ]);
      showSuccessToaster('Order updated');
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving order', () => handleSave());
    }
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle title="Edit Order" onClose={onClose} />
      <DialogBody className={classes.body}>
        <form className={classes.form}>
          <StatusField
            className={classes.input}
            value={orderStatusId}
            onSelect={(update) => setOrderStatusId(update)}
          />
          <ExternalRefField
            className={classes.input}
            value={externalRef}
            onChange={(update) => setExternalRef(update)}
          />
          <DateTimeInput
            disablePast={false}
            className={classes.input}
            label="Created At"
            value={createdAt}
            onChange={setCreatedAt}
          />
        </form>
      </DialogBody>
      <DefaultDialogActions
        disabled={isSaving || !orderStatusId}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
